import { useScreenshotContext } from '@components/ScreenshotProvider/useScreenshotContext';
import DateTime from '@lib/utils/datetime';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import ToastModule from '@modules/ToastModule';
import * as Sentry from '@sentry/react';
import {
  BodyText,
  Button,
  ButtonGroup,
  Headline,
  Link,
  Modal,
  Stack,
  TextArea,
  Tooltip,
} from '@servicetitan/design-system';
import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import { useCallback, useEffect, useState } from 'react';
import FullStoryModule from '@modules/FullStoryModule';
import styles from './BugReportModal.module.css';
import { useReportData } from './useReportData';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { strings } from './strings';
import UserStatusModule from '@modules/UserStatusModule';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import { HelpCenterButton } from '@servicetitan/help-center';
import { useMutation } from '@apollo/client';
import { submitUserReportMutation } from './queries';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';

type Props = {
  setOpen: (open: boolean) => void;
};

function base64ToUint8Array(base64String: string) {
  const binaryString = atob(base64String);
  const uint8Array = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }
  return uint8Array;
}

const BugReportModal: React.FC<Props> = ({ setOpen }) => {
  const auth = useAuth();
  const { image, takeScreenshotOfElement } = useScreenshotContext();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const capturedData = useReportData();
  const {
    [FeatureFlagKeys.featureRefreshUserStatus]: refreshUserStatusEnabled,
    [FeatureFlagKeys.featureMonitorAndResetUserStatus]:
      monitorAndResetUserStatusEnabled,
    [FeatureFlagKeys.featureHelpCenter]: featureHelpCenter,
    [FeatureFlagKeys.featureSendReportToDebugService]:
      featureSendReportToDebugService,
  } = useFeatureFlags();

  useEffect(() => {
    takeScreenshotOfElement();
    SoftPhoneModule.collectWebPhoneDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const getUser = useCallback(() => {
    return (
      auth.user || {
        name: 'Unknown',
        org: {
          id: '',
          name: 'Unknown',
        },
        contactCenterId: '',
        contactCenterUserId: '',
        accountId: '',
      }
    );
  }, [auth]);

  const [submitUserReport] = useMutation(submitUserReportMutation);

  const handleSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      const user = getUser();
      const fileData = JSON.stringify(capturedData, undefined, 2);
      const dt = DateTime(new Date()).format('YYYYMMDDhhmmss');
      const userNameEncoded = encodeURI(auth.user?.name ?? 'Unknown');
      const userFeedback = {
        message,
        name: user.name,
      };
      const userData = {
        captureContext: {
          tags: {
            userName: user.name,
            'org.name': user.org.name,
            'org.id': user.org.id,
            contactCenterId: user.contactCenterId,
            contactCenterUserId: user.contactCenterUserId,
            accountId: user.accountId,
          },
        },
        attachments: [
          {
            filename: `contactcenter-data-${userNameEncoded}-${dt}.json`,
            data: fileData,
            contentType: 'application/json',
          },
          {
            filename: `contactcenter-screenshot-${userNameEncoded}-${dt}.png`,
            data: image
              ? base64ToUint8Array(image.replace('data:image/png;base64,', ''))
              : '',
          },
        ],
      };

      Sentry.captureFeedback(userFeedback, userData);
      if (featureSendReportToDebugService) {
        await submitUserReport({
          variables: {
            input: {
              reportData: fileData,
              fullstoryLink: FullStoryModule.sessionUrl || '',
            },
          },
        });
      }

      setMessage('');
      handleClose();
      ToastModule.showToast({
        title: `Bug report was submitted successfully`,
        status: 'success',
        duration: 5000,
      });

      FullStoryModule.trackEvent('[CCP] Bug Report Submitted', {});
    } catch (error) {
      console.error('Failed to submit bug report', error);
      Sentry.captureException(error);
      ToastModule.showToast({
        title: `Failed to submit bug report`,
        status: 'critical',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    getUser,
    capturedData,
    auth,
    image,
    message,
    handleClose,
    submitUserReport,
    featureSendReportToDebugService,
  ]);

  const disabled = !message || !capturedData || submitting;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(event.target.value);
    },
    [setMessage],
  );

  const handleRefreshStatus = () => {
    if (monitorAndResetUserStatusEnabled) {
      ModalsModule.openModal(ModalId.ResetAccountModal, {
        open: true,
        onClose: () => ModalsModule.closeActiveModal(),
      });
    } else {
      UserStatusModule.attemptRefreshUserStatus();
    }
    setOpen(false);
  };

  return (
    <Modal.Window open={true} size={ModalSizes.S}>
      <Stack
        className='Modal__header p-x-4 p-y-3'
        alignItems='center'
        justifyContent='space-between'
      >
        <Headline className='m-b-0'>{strings.title}</Headline>
      </Stack>
      <Modal.Content className={styles.bugReportModalContent}>
        <Stack direction='column' spacing={3}>
          <div>
            {featureHelpCenter ? (
              <>
                {strings.descriptionWithHelpCenter}{' '}
                <Link onClick={() => setOpen(false)}>
                  <HelpCenterButton className={styles.helpButton} />
                </Link>
              </>
            ) : (
              strings.description
            )}
          </div>

          <TextArea
            placeholder={strings.placeHolder}
            value={message}
            className={styles.messageTextArea}
            autoHeight
            maxRows={5}
            rows={3}
            onChange={handleChange}
          />
          {refreshUserStatusEnabled && (
            <BodyText>
              {strings.connectionIssueTextBefore}
              <Link color='primary' onClick={handleRefreshStatus}>
                {!monitorAndResetUserStatusEnabled
                  ? strings.connectionIssueTextLink
                  : strings.resetAccountTextLink}
              </Link>
              {strings.connectionIssueTextAfter}
            </BodyText>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={handleClose}>{strings.secondaryText}</Button>
          <Tooltip
            direction='top'
            text={!capturedData ? 'Data loading...' : ''}
          >
            <Button
              onClick={handleSubmit}
              disabled={disabled}
              loading={submitting}
            >
              {strings.primaryText}
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Modal.Footer>
    </Modal.Window>
  );
};

export default BugReportModal;
